/**
 * Sport IQ — 6-Week Course landing (list of weeks + days).
 * Renders the modules grouped by week with progress affordances.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, ArrowRight, GraduationCap, CalendarDays, PlayCircle, Lock, Zap } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { getSportMeta } from '../lib/sportMeta';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const BasketballIQCourse = ({ sport = 'basketball' }) => {
  const meta = getSportMeta(sport);
  const ACCENT = meta.accent;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState('');

  useEffect(() => {
    setLoading(true);
    api.get(`/academy/${sport}-iq/course`, { headers: authHeaders() })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [sport]);

  const startCheckout = async (mode) => {
    setBuying(mode);
    try {
      const r = await api.post(`/academy/sport-iq/${sport}/checkout`,
        { mode, origin_url: window.location.origin },
        { headers: authHeaders() });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout failed');
    } finally { setBuying(''); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#050508] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: ACCENT }} />
    </div>
  );

  const weeks = data?.weeks || [];
  const totalModules = data?.total_modules || 0;
  const access = data?.access || { access: false, mode: null, unlocked_week: 0 };
  const pricing = data?.pricing || { drip_monthly: 79, full_onetime: 249 };
  const totalWeeks = data?.total_weeks || 12;

  return (
    <div className="min-h-screen bg-[#050508] text-white pt-16 pb-24" data-testid={`${sport}-iq-course`}>
      {/* Header */}
      <div className="border-b-2 border-zinc-900 py-10 px-6" style={{ background: `linear-gradient(to bottom, ${ACCENT}0A, transparent)` }}>
        <div className="container mx-auto max-w-5xl">
          <Link to={`/${sport}-iq`} className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1 mb-3" data-testid="back-to-hub">
            <ArrowLeft className="w-3 h-3" /> {meta.hub_title}
          </Link>
          <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: ACCENT }}>
            <GraduationCap className="w-3 h-3" /> 12-Week Course
          </div>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight mt-2">
            The 6-Week <span style={{ color: ACCENT }}>Course</span>
          </h1>
          <p className="text-zinc-400 text-sm md:text-base max-w-2xl mt-3">
            ~30 minutes a day. Six weeks. Every module builds on the last.
            Read, absorb, and end the week seeing the game the way Coach B sees it.
          </p>
        </div>
      </div>

      <div className="container mx-auto max-w-5xl px-6 py-10">
        {/* Paywall banner — shown when locked OR when drip mode is active */}
        {(!access.access || access.mode === 'drip') && totalModules > 0 && (
          <div className="border-2 mb-8 p-5" data-testid="course-paywall-banner"
               style={{ borderColor: access.access ? `${ACCENT}44` : ACCENT,
                        backgroundColor: access.access ? `${ACCENT}0A` : `${ACCENT}18` }}>
            {access.access ? (
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[10px] tracking-[0.35em] uppercase font-black" style={{ color: ACCENT }}>
                    Drip Access · Week {access.unlocked_week} of {totalWeeks} Unlocked
                  </div>
                  <p className="text-zinc-300 text-sm mt-1">A new week unlocks every 7 days. Want to skip around now?</p>
                </div>
                <button
                  onClick={() => startCheckout('full')}
                  disabled={buying === 'full'}
                  className="px-4 py-2.5 text-black text-[10px] font-black uppercase tracking-widest disabled:opacity-60"
                  style={{ backgroundColor: ACCENT }}
                  data-testid="upgrade-to-full-btn"
                >
                  {buying === 'full' ? <Loader2 className="w-4 h-4 animate-spin" /> : `Upgrade to Full · $${pricing.full_onetime}`}
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase font-black" style={{ color: ACCENT }}>
                  <Lock className="w-3 h-3" /> {meta.hub_title} · Paid Course
                </div>
                <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight mt-2 text-white">
                  Unlock the Full Course
                </h2>
                <p className="text-zinc-300 text-sm mt-2 max-w-2xl">
                  Every week. Every module. Every glossary term. Two ways in:
                </p>
                <div className="grid md:grid-cols-2 gap-3 mt-4">
                  <button
                    onClick={() => startCheckout('full')}
                    disabled={buying === 'full'}
                    className="flex flex-col items-start gap-1 px-5 py-4 text-black text-left disabled:opacity-60"
                    style={{ backgroundColor: ACCENT }}
                    data-testid="paywall-buy-full"
                  >
                    {buying === 'full' ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                      <>
                        <span className="text-[10px] font-black uppercase tracking-widest opacity-80">One-Time · Full Access</span>
                        <span className="text-2xl font-black leading-none mt-0.5">${pricing.full_onetime}</span>
                        <span className="text-[11px] font-bold uppercase tracking-wider">Skip around · All 12 weeks unlocked now</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => startCheckout('drip')}
                    disabled={buying === 'drip'}
                    className="flex flex-col items-start gap-1 border-2 px-5 py-4 text-left hover:bg-white/5 disabled:opacity-60"
                    style={{ borderColor: ACCENT, color: ACCENT }}
                    data-testid="paywall-buy-drip"
                  >
                    {buying === 'drip' ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                      <>
                        <span className="text-[10px] font-black uppercase tracking-widest opacity-80">Monthly · Drip Access</span>
                        <span className="text-2xl font-black leading-none mt-0.5">${pricing.drip_monthly}<span className="text-sm opacity-80">/mo</span></span>
                        <span className="text-[11px] font-bold uppercase tracking-wider">1 week unlocks every 7 days · Cancel anytime</span>
                      </>
                    )}
                  </button>
                </div>
                <button
                  onClick={() => navigate(`/${sport}-iq/glossary`)}
                  className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-400 hover:text-white mt-4"
                  data-testid="paywall-preview-glossary"
                >
                  <Zap className="w-3 h-3" /> Free Preview: Browse the Full Glossary
                </button>
              </>
            )}
          </div>
        )}

        {totalModules === 0 ? (
          <div className="border-2 border-zinc-800 bg-zinc-950 p-12 text-center" data-testid="course-empty">
            <CalendarDays className="w-10 h-10 mx-auto text-zinc-700 mb-4" />
            <h2 className="text-xl font-black uppercase text-white">Course Coming Soon</h2>
            <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">
              Coach B is authoring the {meta.label} course now. In the meantime,
              dive into the Full Glossary — every term you'll learn here starts there.
            </p>
            <Link to={`/${sport}-iq/glossary`} className="inline-flex items-center gap-2 mt-6 border-2 px-4 py-2 text-[10px] font-black uppercase tracking-widest" style={{ borderColor: ACCENT, color: ACCENT }}>
              Open the Glossary <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        ) : (
          <div className="space-y-8">
            {weeks.map((w) => (
              <div key={w.week} data-testid={`week-${w.week}`}>
                <div className="flex items-baseline gap-3 mb-4">
                  <div className="text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: w.locked ? '#71717a' : ACCENT }}>
                    Week {w.week} {w.locked && <Lock className="inline w-3 h-3 ml-1 align-baseline" />}
                  </div>
                  <div className="h-px flex-1 bg-zinc-900" />
                  <div className="text-[10px] tracking-[0.3em] uppercase font-black text-zinc-600">
                    {w.locked ? (access.access ? `Unlocks Week ${w.week}` : 'Locked') : `${w.days.length} Days`}
                  </div>
                </div>
                <div className="grid gap-3">
                  {w.days.map((d) => (
                    w.locked ? (
                      <div
                        key={d.slug}
                        className="border-2 border-zinc-900 bg-zinc-950/60 p-5 flex items-start gap-4 opacity-50 cursor-not-allowed"
                        data-testid={`day-locked-${d.slug}`}
                      >
                        <div className="flex-shrink-0 w-14 h-14 border-2 border-zinc-800 flex flex-col items-center justify-center">
                          <div className="text-[8px] uppercase tracking-widest font-black text-zinc-600 leading-none">Day</div>
                          <div className="text-2xl font-black leading-none mt-1 text-zinc-600">{d.day}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-black text-zinc-400 text-base md:text-lg leading-tight flex items-center gap-2">
                            <Lock className="w-3.5 h-3.5" /> {d.title}
                          </div>
                          <p className="text-[12px] text-zinc-600 mt-1.5 leading-relaxed line-clamp-2">{d.summary}</p>
                        </div>
                      </div>
                    ) : (
                      <Link
                        key={d.slug}
                        to={`/${sport}-iq/course/${d.slug}`}
                        className="group border-2 border-zinc-800 bg-zinc-950 hover:border-orange-500 transition-all p-5 flex items-start gap-4"
                        data-testid={`day-${d.slug}`}
                        style={{ borderLeftColor: ACCENT, borderLeftWidth: 4 }}
                      >
                        <div className="flex-shrink-0 w-14 h-14 border-2 flex flex-col items-center justify-center" style={{ borderColor: `${ACCENT}66` }}>
                          <div className="text-[8px] uppercase tracking-widest font-black text-zinc-500 leading-none">Day</div>
                          <div className="text-2xl font-black leading-none mt-1" style={{ color: ACCENT }}>{d.day}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-black text-white text-base md:text-lg leading-tight">{d.title}</div>
                          <p className="text-[12px] text-zinc-400 mt-1.5 leading-relaxed line-clamp-2">{d.summary}</p>
                          {d.term_slugs?.length > 0 && (
                            <div className="mt-2 text-[10px] text-zinc-600">
                              {d.term_slugs.length} linked term{d.term_slugs.length === 1 ? '' : 's'}
                            </div>
                          )}
                        </div>
                        <PlayCircle className="w-6 h-6 text-zinc-700 group-hover:text-orange-500 flex-shrink-0" />
                      </Link>
                    )
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BasketballIQCourse;
